// AiCreditsGateView — admin liga/desliga verificação de créditos por
// feature de IA. Master switch + toggles por feature. Quando OFF (default),
// nada é bloqueado e o saldo do aluno é ignorado. [[lib/credits-guard.ts]]

import { useEffect, useMemo, useState } from "preact/hooks";
import {
  AlertTriangle,
  CheckCircle2,
  Coins,
  Loader2,
  Power,
  Save
} from "lucide-react";
import { Switch } from "@app/ui/components/switch";
import { api } from "./api";
import { friendlyError } from "@app/ui/lib/friendly-error";
import { openAlert } from "./ConfirmPromptModal";

interface FeatureMeta {
  slug: string;
  label: string;
  group: string;
  description: string;
}

interface Settings {
  enabledGlobal: boolean;
  features: Record<string, boolean>;
}

export function AiCreditsGateView() {
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [saved, setSaved] = useState<Settings | null>(null);
  const [draft, setDraft] = useState<Settings>({
    enabledGlobal: false,
    features: {}
  });
  const [catalog, setCatalog] = useState<FeatureMeta[]>([]);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  async function load() {
    setPhase("loading");
    setErrMsg(null);
    try {
      const r = await api.getAiCreditsGate();
      setSaved(r.settings);
      setDraft(r.settings);
      setCatalog(r.catalog);
      setPhase("ready");
    } catch (e) {
      setErrMsg(friendlyError(e));
      setPhase("error");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const grouped = useMemo(() => {
    const map = new Map<string, FeatureMeta[]>();
    for (const f of catalog) {
      const list = map.get(f.group) ?? [];
      list.push(f);
      map.set(f.group, list);
    }
    return Array.from(map.entries());
  }, [catalog]);

  function setFeature(slug: string, v: boolean) {
    setDraft({
      ...draft,
      features: { ...draft.features, [slug]: v }
    });
  }

  function setAllInGroup(features: FeatureMeta[], v: boolean) {
    const next = { ...draft.features };
    for (const f of features) next[f.slug] = v;
    setDraft({ ...draft, features: next });
  }

  async function handleSave() {
    setSaving(true);
    try {
      const r = await api.setAiCreditsGate({
        enabledGlobal: draft.enabledGlobal,
        features: draft.features
      });
      setSaved(r.settings);
      setDraft(r.settings);
      setCatalog(r.catalog);
      setSavedAt(Date.now());
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setSaving(false);
    }
  }

  const isDirty = useMemo(() => {
    if (!saved) return false;
    if (saved.enabledGlobal !== draft.enabledGlobal) return true;
    const keys = new Set([
      ...Object.keys(saved.features),
      ...Object.keys(draft.features)
    ]);
    for (const k of keys) {
      const a = saved.features[k] === true;
      const b = draft.features[k] === true;
      if (a !== b) return true;
    }
    return false;
  }, [saved, draft]);

  if (phase === "loading") {
    return (
      <div class="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Carregando configuração…
      </div>
    );
  }
  if (phase === "error") {
    return (
      <div class="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
        {errMsg ?? "Falha ao carregar."}
      </div>
    );
  }

  const enabledCount = Object.values(draft.features).filter((v) => v === true).length;

  return (
    <div class="space-y-6">
      <div class="rounded-md border border-violet-500/30 bg-violet-500/5 p-4">
        <div class="flex items-start gap-3">
          <Coins className="mt-0.5 size-5 shrink-0 text-violet-600" />
          <div class="space-y-1 text-sm">
            <h3 class="font-bold">Verificação de créditos por feature de IA</h3>
            <p class="text-muted-foreground">
              Cada chamada de IA que a plataforma paga pode ser condicionada
              ao saldo de créditos do aluno. Hoje, por padrão, nada é
              verificado — toda chamada passa direto. Ligue só as features que
              você quer cobrar. Chamadas com chave própria do aluno (BYOK) ou
              via Google connected <strong>nunca</strong> caem nesta regra.
            </p>
            <p class="text-muted-foreground">
              Atalho de duas chaves: <strong>desligado globalmente</strong>{" "}
              libera tudo; <strong>ligado globalmente</strong> aplica só nas
              features marcadas abaixo.
            </p>
          </div>
        </div>
      </div>

      <section class="space-y-4 rounded-md border border-border bg-card p-5">
        <header class="flex items-center gap-2">
          <Power className="size-4 text-primary" />
          <h2 class="text-sm font-bold">Master switch</h2>
        </header>
        <label class="flex items-start justify-between gap-3 rounded-md border border-border bg-background/50 px-3 py-2.5">
          <div class="min-w-0 flex-1">
            <div class="text-sm font-semibold">
              {draft.enabledGlobal
                ? "Verificação ATIVA — features marcadas exigem saldo"
                : "Verificação DESLIGADA — tudo livre"}
            </div>
            <div class="mt-0.5 text-[11px] text-muted-foreground">
              Quando desligado, o saldo de créditos do aluno é ignorado em
              todas as chamadas de IA da plataforma. Telemetria de gasto
              (tokens) continua sendo gravada normalmente.
            </div>
          </div>
          <Switch
            checked={draft.enabledGlobal}
            onCheckedChange={(v) => setDraft({ ...draft, enabledGlobal: v })}
          />
        </label>
        {draft.enabledGlobal && enabledCount === 0 && (
          <div class="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-700 dark:text-amber-300">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
            Master switch ligado mas nenhuma feature está marcada — efeito
            prático é o mesmo de "desligado". Ligue ao menos uma feature
            abaixo pra começar a verificar saldo.
          </div>
        )}
      </section>

      {grouped.map(([groupName, features]) => {
        const onCount = features.filter((f) => draft.features[f.slug] === true).length;
        const allOn = onCount === features.length;
        return (
          <section
            key={groupName}
            class="space-y-3 rounded-md border border-border bg-card p-5"
          >
            <header class="flex items-center justify-between gap-3">
              <h2 class="text-sm font-bold">{groupName}</h2>
              <div class="flex items-center gap-2">
                <span class="text-[11px] text-muted-foreground">
                  {onCount}/{features.length} ligadas
                </span>
                <button
                  type="button"
                  onClick={() => setAllInGroup(features, !allOn)}
                  class="text-[11px] font-medium text-violet-600 hover:underline dark:text-violet-400"
                >
                  {allOn ? "Desligar tudo" : "Ligar tudo"}
                </button>
              </div>
            </header>
            <div class="space-y-2">
              {features.map((f) => (
                <label
                  key={f.slug}
                  class="flex items-start justify-between gap-3 rounded-md border border-border bg-background/50 px-3 py-2.5"
                >
                  <div class="min-w-0 flex-1">
                    <div class="text-sm font-semibold">{f.label}</div>
                    <div class="mt-0.5 text-[11px] text-muted-foreground">
                      {f.description}
                    </div>
                    <code class="mt-1 inline-block text-[10px] text-muted-foreground/70">
                      {f.slug}
                    </code>
                  </div>
                  <Switch
                    checked={draft.features[f.slug] === true}
                    onCheckedChange={(v) => setFeature(f.slug, v)}
                  />
                </label>
              ))}
            </div>
          </section>
        );
      })}

      <div class="sticky bottom-4 z-10 flex items-center gap-3 rounded-md border border-border bg-card/95 px-4 py-2 backdrop-blur">
        <button
          type="button"
          onClick={handleSave}
          disabled={!isDirty || saving}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <Save className="size-3.5" />
          )}
          {saving ? "Salvando…" : "Salvar alterações"}
        </button>
        {isDirty && (
          <button
            type="button"
            onClick={() => saved && setDraft(saved)}
            disabled={saving}
            class="text-xs font-medium text-muted-foreground hover:text-foreground hover:underline disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancelar
          </button>
        )}
        {savedAt && !isDirty && (
          <span class="inline-flex items-center gap-1 text-[11px] text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 className="size-3.5" /> salvo
          </span>
        )}
      </div>
    </div>
  );
}
